import React from 'react';
import { Button, Container, Row, Col, Form, Modal } from 'react-bootstrap';
import { useMsal, MsalAuthenticationTemplate } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import Select from 'react-select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faSave } from '@fortawesome/free-solid-svg-icons';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import InformationModal from '../../Components/InformationModal/InformationModal';
import { AccountCostingCodeMapApi, SapMasterApi } from '../../Services/ConciliationApiDataService';
import ServerTable from '../../Components/Table/ServerTabe';
import { getStoredConciliationCompanyId, setStoredConciliationCompanyId } from './companySelectionStore';
import {
  costingCodeSummary,
  costingCodeOptionsByDimension,
  normalizeCostingCodeMap,
  normalizeCostingDimensions,
  setCostingCodeValue
} from './costingCodeUtils';

const ACCOUNT_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'TARJETACREDITO', label: 'Credit Card' },
  { value: 'BANCO', label: 'Bank Account' }
];
const EMPTY_COMPANY_OPTION = { value: null, label: 'Select company...' };

/**
 * @typedef {Object} AccountCostingCodeCandidateDto
 * @property {string=} last4
 * @property {string=} accountType
 * @property {string=} accountIdentifier
 * @property {string=} description
 * @property {number|string|null=} mappingId
 * @property {Record<number,string|null>=} costingCodes
 * @property {string|null=} cashAccount
 * @property {boolean=} isActive
 */

const normalizeAccountType = (value) => {
  const t = (value ?? '').toString().trim().toUpperCase();
  if (t === 'BANCO') return 'BANCO';
  if (t === 'TARJETACREDITO') return 'TARJETACREDITO';
  return 'TARJETACREDITO';
};

const extractLast4 = (value) => {
  const digits = (value ?? '').toString().replace(/\D/g, '');
  if (!digits) return null;
  return digits.slice(-4);
};

const rowKeyOf = ({ accountType, accountIdentifier, last4 }) => {
  const type = normalizeAccountType(accountType);
  const identifier = accountIdentifier ?? last4 ?? '';
  return `${type}|${identifier}`;
};

const costingCodesEqual = (a, b) => {
  const left = normalizeCostingCodeMap(a);
  const right = normalizeCostingCodeMap(b);
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  for (const key of keys) {
    if ((left[key] ?? null) !== (right[key] ?? null)) return false;
  }
  return true;
};

const hasAnyCostingCode = (codes) =>
  Object.values(normalizeCostingCodeMap(codes)).some((value) => !!value);

const AccountCostingCodeMapPageContent = () => {
  const { instance, accounts } = useMsal();

  const [companyId, setCompanyId] = React.useState(() => getStoredConciliationCompanyId());
  const [companyOptions, setCompanyOptions] = React.useState([]);
  const [loadingCompanies, setLoadingCompanies] = React.useState(false);

  const [costingOptionsByDimension, setCostingOptionsByDimension] = React.useState({});
  const [costingDimensions, setCostingDimensions] = React.useState([]);
  const [cashAccountOptions, setCashAccountOptions] = React.useState([]);
  const [loadingMasters, setLoadingMasters] = React.useState(false);
  const loadedMastersForCompanyRef = React.useRef(new Set());

  const [baseMapCache, setBaseMapCache] = React.useState({});
  const [mapCache, setMapCache] = React.useState({});
  const mapCacheRef = React.useRef({});
  const [dirtyRows, setDirtyRows] = React.useState(new Set());

  const [refreshTick, setRefreshTick] = React.useState(0);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [infoModal, setInfoModal] = React.useState({ show: false, title: '', content: null });

  const [unsavedModal, setUnsavedModal] = React.useState({ show: false, saving: false, error: '' });
  const unsavedResolverRef = React.useRef(null);

  React.useEffect(() => {
    mapCacheRef.current = mapCache;
  }, [mapCache]);

  const showInfoModal = React.useCallback((title, content) => {
    setInfoModal({ show: true, title, content });
  }, []);

  const hideInfoModal = React.useCallback(() => {
    setInfoModal((prev) => ({ ...prev, show: false }));
  }, []);

  const acquireToken = React.useCallback(async () => {
    const req = { ...loginRequest, account: accounts[0] };
    const { accessToken } = await instance.acquireTokenSilent(req);
    return accessToken;
  }, [instance, accounts]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingCompanies(true);
        const token = await acquireToken();
        const arr = await SapMasterApi.getCompanies(token);
        if (cancelled) return;
        const opts = (arr ?? []).map((c) => ({
          value: c.companyId,
          label: c.companyName ?? `Company ${c.companyId}`,
          raw: c
        }));
        setCompanyOptions(opts);
        if (companyId && !opts.some((o) => o.value === companyId)) {
          setCompanyId(null);
          setStoredConciliationCompanyId(null);
        }
      } catch (err) {
        if (!cancelled) console.error('Failed to load companies', err);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  // Company validity is intentionally checked against this initial company load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acquireToken]);

  React.useEffect(() => {
    if (!companyId || loadedMastersForCompanyRef.current.has(companyId)) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingMasters(true);
        const token = await acquireToken();
        const [glRes, costingCodeRes, dimensionRes] = await Promise.all([
          SapMasterApi.getGlAccounts(companyId, { search: '', page: 1, pageSize: 4000 }, token),
          SapMasterApi.getCostingCodes(companyId, { search: '', page: 1, pageSize: 5000 }, token),
          SapMasterApi.getCostingDimensions(companyId, token)
        ]);
        const dimensions = normalizeCostingDimensions(dimensionRes?.items ?? dimensionRes);

        if (cancelled) return;
        setCostingDimensions(dimensions);
        setCostingOptionsByDimension(costingCodeOptionsByDimension(costingCodeRes.items ?? []));
        setCashAccountOptions((glRes.items.filter(x => x.cashAccout === "Y") ?? []).map((a) => ({
          value: a.code,
          label: `${a.formatCode ?? a.code} - ${a.name ?? ''}`
        })));
        loadedMastersForCompanyRef.current.add(companyId);
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load masters', err);
          setCostingDimensions([]);
          setCostingOptionsByDimension({});
          showInfoModal(
            'Load failed',
            <p className="mb-0">Account and costing-code master data could not be loaded. Dynamic costing-dimension columns are unavailable.</p>
          );
        }
      } finally {
        if (!cancelled) setLoadingMasters(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [companyId, acquireToken, showInfoModal]);

  const findCostingOption = React.useCallback(
    (dimensionCode, code) => {
      const options = costingOptionsByDimension[Number(dimensionCode)] ?? [];
      return code ? options.find((o) => o.value === code) ?? { value: code, label: code } : null;
    },
    [costingOptionsByDimension]
  );
  const findCashOption = React.useCallback(
    (code) => (code ? cashAccountOptions.find((o) => o.value === code) ?? null : null),
    [cashAccountOptions]
  );

  const reconcileDirty = React.useCallback(
    (rowKey, next) => {
      const base = baseMapCache[rowKey] || { costingCodes: {}, cashAccount: null, isActive: true };
      const isDirty =
        !costingCodesEqual(base.costingCodes, next.costingCodes) ||
        (base.cashAccount ?? null) !== (next.cashAccount ?? null) ||
        (base.isActive ?? true) !== (next.isActive ?? true);

      setDirtyRows((prev) => {
        const clone = new Set(prev);
        if (isDirty) clone.add(rowKey);
        else clone.delete(rowKey);
        return clone;
      });
    },
    [baseMapCache]
  );

  const fetchData = React.useCallback(
    async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter }) => {
      void refreshTick;
      const token = await acquireToken();

      const filters = Object.fromEntries(
        (columnFilters ?? [])
          .filter((filter) => filter?.value !== undefined && filter?.value !== null && filter.value !== '')
          .map((filter) => [filter.id, filter.value])
      );
      const costingCodeFilters = Object.entries(filters)
        .filter(([id]) => /^costingCode\d+$/.test(id))
        .map(([id, value]) => ({ dimensionCode: Number(id.replace('costingCode', '')), value }));

      const sortByMap = {
        Last4: 'last4',
        AccountType: 'accountType',
        AccountIdentifier: 'accountIdentifier',
        Description: 'description',
        CashAccount: 'cashAccount'
      };
      const sort = sorting?.[0];
      const costingSortMatch = sort?.id?.match(/^costingCode(\d+)$/);
      const sortBy = costingSortMatch ? 'costingCode' : (sortByMap[sort?.id] ?? 'last4');
      const sortDimension = costingSortMatch ? Number(costingSortMatch[1]) : undefined;
      const sortDir = sort?.desc ? 'desc' : 'asc';

      const res = await AccountCostingCodeMapApi.getCandidates(
        companyId,
        {
          search: globalFilter || undefined,
          page: pageIndex + 1,
          pageSize,
          sortBy,
          sortDimension,
          sortDir,
          accountType: filters.AccountType || undefined,
          accountIdentifier: filters.AccountIdentifier || undefined,
          last4: filters.Last4 || undefined,
          description: filters.Description || undefined,
          cashAccount: filters.CashAccount || undefined,
          costingCodeFilters: costingCodeFilters.length ? JSON.stringify(costingCodeFilters) : undefined
        },
        token
      );

      const rows = (res.items ?? []).map((r) => {
        const accountType = normalizeAccountType(r.accountType);
        const accountIdentifier = r.accountIdentifier ?? r.last4 ?? null;
        return {
          ...r,
          accountType,
          accountIdentifier,
          last4: r.last4 ?? extractLast4(accountIdentifier),
          rowKey: rowKeyOf({ accountType, accountIdentifier, last4: r.last4 })
        };
      });

      const seededBase = {};
      const seededCur = {};
      rows.forEach((r) => {
        const costingCodes = normalizeCostingCodeMap(r.costingCodes);
        const cur = {
          rowKey: r.rowKey,
          id: r.mappingId ?? null,
          accountType: r.accountType,
          accountIdentifier: r.accountIdentifier,
          last4: r.last4 ?? extractLast4(r.accountIdentifier),
          description: r.description ?? null,
          costingCodes,
          cashAccount: r.cashAccount ?? null,
          isActive: r.isActive ?? true
        };
        seededBase[r.rowKey] = cur;
        seededCur[r.rowKey] = mapCacheRef.current[r.rowKey] ?? cur;
      });

      setBaseMapCache((prev) => ({ ...prev, ...seededBase }));
      setMapCache((prev) => ({ ...seededCur, ...prev }));

      const pageKeys = new Set(rows.map((r) => r.rowKey));
      setDirtyRows((prev) => {
        const next = new Set(prev);
        for (const k of pageKeys) {
          const base = seededBase[k];
          const cur = seededCur[k] ?? base;
          const isDirty =
            !costingCodesEqual(base.costingCodes, cur.costingCodes) ||
            (base.cashAccount ?? null) !== (cur.cashAccount ?? null) ||
            (base.isActive ?? true) !== (cur.isActive ?? true);
          if (isDirty) next.add(k);
          else next.delete(k);
        }
        return next;
      });

      return {
        items: rows,
        totalItems: res.totalItems ?? 0
      };
    },
    [companyId, acquireToken, refreshTick]
  );

  const setCostingFor = React.useCallback(
    (rowKey, dimensionCode, code) => {
      setMapCache((prev) => {
        const cur = prev[rowKey] ?? { id: null, costingCodes: {}, isActive: true };
        const costingCodes = setCostingCodeValue(cur.costingCodes, dimensionCode, code);
        const next = { ...cur, costingCodes };
        setTimeout(() => reconcileDirty(rowKey, next), 0);
        return { ...prev, [rowKey]: next };
      });
    },
    [reconcileDirty]
  );

  const setCashFor = React.useCallback(
    (rowKey, code) => {
      setMapCache((prev) => {
        const cur = prev[rowKey] ?? { id: null, costingCodes: {}, cashAccount: null, isActive: true };
        const next = { ...cur, cashAccount: code };
        setTimeout(() => reconcileDirty(rowKey, next), 0);
        return { ...prev, [rowKey]: next };
      });
    },
    [reconcileDirty]
  );

  const handleCompanyChange = async (opt) => {
    const id = opt?.value ?? null;
    if (id === companyId) return;
    if (!(await confirmUnsavedQueryChange())) return;
    setCompanyId(id);
    setStoredConciliationCompanyId(id);
    setBaseMapCache({});
    setMapCache({});
    setCostingDimensions([]);
    setCostingOptionsByDimension({});
    if (id) loadedMastersForCompanyRef.current.delete(id);
    setDirtyRows(new Set());
    setRefreshTick((t) => t + 1);
  };

  const handleRefresh = async () => {
    if (!(await confirmUnsavedQueryChange())) return;
    setRefreshTick((t) => t + 1);
  };

  const openConfirm = React.useCallback(() => {
    if (dirtyRows.size === 0) return;
    setShowConfirm(true);
  }, [dirtyRows]);

  const saveDirtyRows = React.useCallback(async ({ showSuccess = true, showError = true } = {}) => {
    try {
      const token = await acquireToken();
      const dirtyList = Array.from(dirtyRows);

      const payloads = dirtyList.map((k) => ({ rowKey: k, ...mapCache[k] }));

      const savedRows = [];
      for (const p of payloads) {
        const accountIdentifier = p.accountIdentifier ?? '';
        const saved = await AccountCostingCodeMapApi.upsertAccountCostingCodeMap(
          companyId,
          {
            id: p.id ?? null,
            accountType: normalizeAccountType(p.accountType),
            accountIdentifier,
            last4: p.last4 ?? extractLast4(accountIdentifier),
            costingCodes: normalizeCostingCodeMap(p.costingCodes),
            cashAccount: p.cashAccount ?? null,
            isActive: p.isActive ?? true
          },
          token
        );
        const returnedId = saved?.id ?? saved?.mappingId ?? (typeof saved === 'number' || typeof saved === 'string' ? saved : null);
        savedRows.push({ ...p, id: returnedId ?? p.id ?? null });
      }

      setBaseMapCache((prev) => {
        const clone = { ...prev };
        for (const p of savedRows) {
          clone[p.rowKey] = {
            id: p.id,
            rowKey: p.rowKey,
            accountType: normalizeAccountType(p.accountType),
            accountIdentifier: p.accountIdentifier ?? null,
            last4: p.last4 ?? extractLast4(p.accountIdentifier),
            description: p.description ?? null,
            costingCodes: normalizeCostingCodeMap(p.costingCodes),
            cashAccount: p.cashAccount ?? null,
            isActive: p.isActive ?? true
          };
        }
        return clone;
      });
      setMapCache((prev) => {
        const clone = { ...prev };
        for (const p of savedRows) clone[p.rowKey] = { ...clone[p.rowKey], id: p.id };
        return clone;
      });

      setDirtyRows((prev) => {
        const next = new Set(prev);
        for (const p of payloads) next.delete(p.rowKey);
        return next;
      });

      if (showSuccess) {
        showInfoModal(
          'Mappings saved',
          <div>
            <p className="mb-0"><strong>{payloads.length}</strong> mapping(s) saved successfully.</p>
          </div>
        );
      }
      return true;
    } catch (err) {
      console.error('Save all failed', err);
      if (showError) showInfoModal('Save failed', <p className="mb-0">Some mappings could not be saved. Please try again.</p>);
      return false;
    }
  }, [dirtyRows, mapCache, companyId, acquireToken, showInfoModal]);

  const handleConfirmSaveAll = React.useCallback(async () => {
    await saveDirtyRows();
    setShowConfirm(false);
  }, [saveDirtyRows]);

  const confirmUnsavedQueryChange = React.useCallback(() => {
    if (dirtyRows.size === 0) return Promise.resolve(true);
    return new Promise((resolve) => {
      unsavedResolverRef.current = resolve;
      setUnsavedModal({ show: true, saving: false, error: '' });
    });
  }, [dirtyRows]);

  const settleUnsavedQueryChange = React.useCallback((allowed) => {
    const resolve = unsavedResolverRef.current;
    unsavedResolverRef.current = null;
    setUnsavedModal({ show: false, saving: false, error: '' });
    resolve?.(allowed);
  }, []);

  const discardUnsavedAndContinue = React.useCallback(() => {
    const restored = { ...baseMapCache };
    mapCacheRef.current = restored;
    setMapCache(restored);
    setDirtyRows(new Set());
    settleUnsavedQueryChange(true);
  }, [baseMapCache, settleUnsavedQueryChange]);

  const saveUnsavedAndContinue = React.useCallback(async () => {
    setUnsavedModal((prev) => ({ ...prev, saving: true, error: '' }));
    const saved = await saveDirtyRows({ showSuccess: false, showError: false });
    if (saved) {
      settleUnsavedQueryChange(true);
    } else {
      setUnsavedModal((prev) => ({ ...prev, saving: false, error: 'The changes could not be saved. Please try again or cancel.' }));
    }
  }, [saveDirtyRows, settleUnsavedQueryChange]);

  const columns = React.useMemo(
    () => [
      {
        header: 'Type',
        accessorKey: 'accountType',
        id: 'AccountType',
        meta: {
          filterVariant: 'select',
          filterOptions: ACCOUNT_TYPE_OPTIONS.filter((option) => option.value),
          headerStyle: { width: '110px', minWidth: '110px' },
          filterStyle: { width: '110px', minWidth: '110px' },
          cellStyle: { width: '110px', minWidth: '110px' }
        },
        cell: ({ row }) => (normalizeAccountType(row.original.accountType) === 'BANCO' ? 'Bank Account' : 'Credit Card')
      },
      {
        header: 'Account Identifier',
        accessorKey: 'accountIdentifier',
        id: 'AccountIdentifier',
        meta: {
          headerStyle: { width: '180px', minWidth: '180px' },
          filterStyle: { width: '180px', minWidth: '180px' },
          cellStyle: { width: '180px', minWidth: '180px' }
        },
        cell: ({ row }) => row.original.accountIdentifier ?? row.original.last4 ?? '-'
      },
      {
        header: 'Last4',
        accessorKey: 'last4',
        id: 'Last4',
        meta: {
          headerStyle: { width: '80px', minWidth: '80px' },
          filterStyle: { width: '80px', minWidth: '80px' },
          cellStyle: { width: '80px', minWidth: '80px' }
        },
        cell: ({ row }) => row.original.last4 ?? extractLast4(row.original.accountIdentifier) ?? '-'
      },
      {
        header: 'Description',
        accessorKey: 'description',
        id: 'Description',
        meta: {
          headerStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' },
          filterStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' },
          cellStyle: { width: '220px', minWidth: '220px', maxWidth: '220px' }
        },
        cell: ({ row }) => {
          const value = row.original.description ?? '-';
          return (
            <div className="text-truncate" style={{ maxWidth: 220 }} title={value}>
              {value}
            </div>
          );
        }
      },
      {
        header: 'Cash Account',
        accessorKey: 'cashAccount',
        id: 'CashAccount',
        meta: {
          headerStyle: { width: '320px', minWidth: '320px' },
          filterStyle: { width: '320px', minWidth: '320px' },
          cellStyle: { width: '320px', minWidth: '320px' }
        },
        cell: ({ row }) => {
          const rowKey = row.original.rowKey ?? rowKeyOf(row.original);
          const cached = mapCache[rowKey] || { cashAccount: null };
          return (
            <Select
              classNamePrefix="react-select"
              isDisabled={loadingMasters}
              options={cashAccountOptions}
              value={findCashOption(cached.cashAccount)}
              onChange={(opt) => setCashFor(rowKey, opt?.value ?? null)}
              isClearable
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          );
        }
      },
      ...costingDimensions.map((dimension) => ({
        header: dimension.displayName ?? `Costing Code ${dimension.dimensionCode}`,
        id: `costingCode${dimension.dimensionCode}`,
        accessorFn: (row) => normalizeCostingCodeMap(row.costingCodes)?.[Number(dimension.dimensionCode)] ?? null,
        meta: {
          headerStyle: { width: '320px', minWidth: '320px' },
          filterStyle: { width: '320px', minWidth: '320px' },
          cellStyle: { width: '320px', minWidth: '320px' }
        },
        cell: ({ row }) => {
          const rowKey = row.original.rowKey ?? rowKeyOf(row.original);
          const cached = mapCache[rowKey] || { costingCodes: {}, isActive: true };
          const dimensionCode = Number(dimension.dimensionCode);
          const options = costingOptionsByDimension[dimensionCode] ?? [];
          const value = cached.costingCodes?.[dimensionCode] ?? null;
          return options.length > 0 ? (
            <Select
              classNamePrefix="react-select"
              isDisabled={loadingMasters}
              options={options}
              value={findCostingOption(dimensionCode, value)}
              onChange={(opt) => setCostingFor(rowKey, dimensionCode, opt?.value ?? null)}
              isClearable
              placeholder="Select..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          ) : (
            <Form.Control
              value={value ?? ''}
              onChange={(e) => setCostingFor(rowKey, dimensionCode, e.target.value)}
              placeholder="Enter code"
              disabled={loadingMasters}
            />
          );
        }
      }))
    ],
    [cashAccountOptions, loadingMasters, mapCache, setCashFor, findCashOption, costingDimensions, costingOptionsByDimension, findCostingOption, setCostingFor]
  );

  const previewItems = React.useMemo(() => {
    const out = [];
    for (const rowKey of Array.from(dirtyRows)) {
      const base = baseMapCache[rowKey] ?? { costingCodes: {}, cashAccount: null, isActive: true };
      const cur = mapCache[rowKey] ?? base;
      out.push({
        rowKey,
        accountType: normalizeAccountType(cur.accountType),
        accountIdentifier: cur.accountIdentifier ?? '-',
        last4: cur.last4 ?? extractLast4(cur.accountIdentifier) ?? '-',
        costingFrom: costingCodeSummary(base.costingCodes, costingDimensions) || '(none)',
        costingTo: costingCodeSummary(cur.costingCodes, costingDimensions) || '(none)',
        cashFrom: base.cashAccount ?? '(none)',
        cashTo: cur.cashAccount ?? '(none)',
        active: cur.isActive ? 'Yes' : 'No'
      });
    }
    return out.sort((a, b) => `${a.accountType}-${a.accountIdentifier}`.localeCompare(`${b.accountType}-${b.accountIdentifier}`));
  }, [dirtyRows, baseMapCache, mapCache, costingDimensions]);

  const selectedCompany = React.useMemo(() => companyOptions.find((o) => o.value === companyId) ?? null, [companyOptions, companyId]);

  return (
    <Container fluid className="h-100 d-flex flex-column">
      <div className="d-flex justify-content-between align-items-center w-100 mb-2">
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <div style={{ minWidth: 280 }}>
            <Select
              classNamePrefix="react-select"
              options={[EMPTY_COMPANY_OPTION, ...companyOptions]}
              value={selectedCompany}
              onChange={handleCompanyChange}
              isLoading={loadingCompanies}
              placeholder="Select company..."
              menuPortalTarget={document.body}
              styles={{ menuPortal: (b) => ({ ...b, zIndex: 9999 }) }}
            />
          </div>
        </div>

        <div className="d-flex">
          <button className="btn btn-xl btn-outline-primary fw-bold me-2" onClick={handleRefresh} disabled={!companyId} title="Refresh">
            <FontAwesomeIcon icon={faRefresh} />
          </button>

          <button
            className="btn btn-xl btn-primary fw-bold"
            onClick={() => openConfirm()}
            disabled={dirtyRows.size === 0 || !companyId}
            title={dirtyRows.size ? `Save ${dirtyRows.size} change(s)` : 'No changes to save'}
          >
            <FontAwesomeIcon icon={faSave} className="me-1" />
            Save All
          </button>
        </div>
      </div>

      <Row className="my-3">
        <Col sm={12}>
          {!companyId && (
            <div className="alert alert-info mb-3">
              Select a company to load card/account mapping records.
            </div>
          )}
          <ServerTable
            columns={columns}
            fetchData={fetchData}
            tableClass="table-hover"
            containerClass="table-responsive"
            headerClass="table-light"
            enableGlobalFilter={false}
            initialPageSize={10}
            onBeforeQueryChange={confirmUnsavedQueryChange}
          />
        </Col>
      </Row>

      <ConfirmationModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        title="Confirm mappings to save"
        content={
          <div>
            <p className="mb-2">You are about to save <strong>{previewItems.length}</strong> mapping(s).</p>
            <div className="table-responsive">
              <table className="table table-sm table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Account Type</th>
                    <th>Account Identifier</th>
                    <th>Last4</th>
                    <th>Costing Codes (current)</th>
                    <th>Costing Codes (new)</th>
                    <th>Cash Account (current)</th>
                    <th>Cash Account (new)</th>
                    <th>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {previewItems.map((it) => (
                    <tr key={it.rowKey}>
                      <td>{it.accountType === 'BANCO' ? 'Bank Account' : 'Credit Card'}</td>
                      <td>{it.accountIdentifier}</td>
                      <td>{it.last4}</td>
                      <td>{it.costingFrom}</td>
                      <td>{it.costingTo}</td>
                      <td>{it.cashFrom}</td>
                      <td>{it.cashTo}</td>
                      <td>{it.active}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        }
        onAcceptClick={handleConfirmSaveAll}
        onCancelClick={() => setShowConfirm(false)}
      />
      <Modal
        show={unsavedModal.show}
        onHide={() => !unsavedModal.saving && settleUnsavedQueryChange(false)}
        backdrop={unsavedModal.saving ? 'static' : true}
        keyboard={!unsavedModal.saving}
        centered
      >
        <Modal.Header closeButton={!unsavedModal.saving}>
          <Modal.Title>Unsaved mapping changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            You have <strong>{dirtyRows.size}</strong> unsaved mapping change(s). Changing the sort, filters, page, company, or refreshing may discard those edits.
          </p>
          <p className="mb-0">Save the changes before continuing, continue without saving, or cancel to keep editing.</p>
          {unsavedModal.error && <div className="alert alert-danger mt-3 mb-0">{unsavedModal.error}</div>}
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="outline-secondary" disabled={unsavedModal.saving} onClick={() => settleUnsavedQueryChange(false)}>
            Cancel
          </Button>
          <div className="d-flex gap-2">
            <Button variant="outline-danger" disabled={unsavedModal.saving} onClick={discardUnsavedAndContinue}>
              Continue without saving
            </Button>
            <Button variant="primary" disabled={unsavedModal.saving} onClick={saveUnsavedAndContinue}>
              {unsavedModal.saving ? 'Saving...' : 'Save and continue'}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
      <InformationModal
        show={infoModal.show}
        onHide={hideInfoModal}
        onAcceptClick={hideInfoModal}
        title={infoModal.title}
        content={infoModal.content}
      />
    </Container>
  );
};

const AccountCostingCodeMapPage = () => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title="Card / Account -> Costing Codes">
      <AccountCostingCodeMapPageContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default AccountCostingCodeMapPage;
